import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  STEP_BY_ID,
  STEP_CATALOG,
  missingCoherenceFiles,
  resolveDependencies,
  type StepCategory,
  type StepDef,
} from '../stepCatalog';

interface StepSelectorProps {
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Noms logiques des fichiers fournis (détermine ce qui est importable). */
  presentFiles: Set<string>;
  /** Mode avancé : montre toutes les catégories + détails techniques. */
  advanced?: boolean;
}

/**
 * Sélection de ce qu'on importe, pilotée par les fichiers fournis.
 *
 * Seules les entités dont le fichier est présent sont cochables. Cocher une
 * entité tire ses prérequis : si l'un d'eux n'a pas son fichier, une alerte
 * d'impossibilité s'affiche (cohérence d'un déploiement « école neuve »).
 *
 * - Mode simple : uniquement les entités métier (catégorie import).
 * - Mode avancé : toutes les catégories + dépendances + fichiers attendus.
 */
export function StepSelector({ selected, onChange, presentFiles, advanced = false }: StepSelectorProps) {
  // Steps requis (dépendance) d'un step explicitement choisi → verrouillés.
  const requiredByOthers = new Set<string>();
  for (const id of selected) {
    for (const dep of STEP_BY_ID[id]?.dependencies ?? []) {
      if (dep !== id) requiredByOthers.add(dep);
    }
  }

  const missing = missingCoherenceFiles(selected, presentFiles);

  const hasFile = (step: StepDef) => step.requiredFiles.every((f) => presentFiles.has(f.logicalName));

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      if (requiredByOthers.has(id)) return; // verrouillé : dépendance d'un autre
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange(resolveDependencies(next));
  };

  const renderRow = (step: StepDef) => {
    const isSelected = selected.has(step.identifier);
    const needsFile = step.requiredFiles.length > 0;
    const fileReady = hasFile(step);
    const fileMissing = needsFile && !fileReady;
    const isDepLock = isSelected && requiredByOthers.has(step.identifier);
    // Fichier absent → non cochable. S'il est tiré comme dépendance, il reste
    // affiché coché mais verrouillé, avec l'alerte de cohérence ci-dessus.
    const disabled = fileMissing || isDepLock;

    return (
      <label
        key={step.identifier}
        className={`flex items-start gap-3 rounded-md border border-transparent p-2 ${
          disabled && !isSelected ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted/50'
        }`}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => toggle(step.identifier)}
          disabled={disabled}
          className="mt-1"
        />
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{step.name}</span>

            {fileMissing && isSelected && (
              <span className="flex items-center gap-1 text-xs font-medium text-destructive">
                <AlertTriangle className="h-3 w-3" /> fichier requis manquant
              </span>
            )}
            {fileMissing && !isSelected && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <AlertTriangle className="h-3 w-3" /> fichier absent
              </span>
            )}
            {needsFile && fileReady && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <CheckCircle2 className="h-3 w-3" /> fichier prêt
              </span>
            )}

            {advanced && step.requiredFiles.length > 0 && (
              <Badge variant="outline" className="text-xs">
                {step.requiredFiles.map((f) => `${f.logicalName}.csv`).join(', ')}
              </Badge>
            )}
            {isDepLock && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Lock className="h-3 w-3" /> requis
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{step.description}</p>
          {advanced && step.dependencies.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground/80">
              Dépend de : {step.dependencies.map((d) => STEP_BY_ID[d]?.name ?? d).join(', ')}
            </p>
          )}
        </div>
      </label>
    );
  };

  const coherenceAlert =
    missing.length > 0 ? (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-medium">Import impossible en l’état : des fichiers requis manquent.</p>
          <ul className="mt-1 list-inside list-disc text-sm">
            {missing.map(({ stepId, spec }) => (
              <li key={`${stepId}:${spec.logicalName}`}>
                <span className="font-mono">{spec.logicalName}.csv</span> — requis pour «&nbsp;
                {STEP_BY_ID[stepId]?.name ?? stepId}&nbsp;»
              </li>
            ))}
          </ul>
          <p className="mt-1 text-sm">
            Revenez à l’étape « Fichiers » pour les ajouter, ou décochez les imports concernés.
          </p>
        </AlertDescription>
      </Alert>
    ) : null;

  // ---- Mode simple : une seule carte, entités métier (import) ----
  if (!advanced) {
    const importSteps = STEP_CATALOG.filter((s) => s.category === 'import');
    return (
      <div className="space-y-4">
        {coherenceAlert}
        <Card>
          <CardHeader icon={<FileText />} iconColor="blue">
            <CardTitle className="text-base">Que voulez-vous importer ?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="mb-2 text-sm text-muted-foreground">
              D’après vos fichiers, voici ce qui peut être importé. Seules les entités dont le
              fichier est présent sont disponibles.
            </p>
            {importSteps.map(renderRow)}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Mode avancé : toutes les catégories ----
  const byCategory = (cat: StepCategory) => STEP_CATALOG.filter((s) => s.category === cat);

  return (
    <div className="space-y-6">
      {coherenceAlert}
      {CATEGORY_ORDER.map((cat) => {
        const steps = byCategory(cat);
        if (steps.length === 0) return null;
        return (
          <Card key={cat}>
            <CardHeader icon={<FileText />} iconColor="blue">
              <CardTitle className="text-base">{CATEGORY_LABELS[cat]}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">{steps.map(renderRow)}</CardContent>
          </Card>
        );
      })}
    </div>
  );
}
